import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Offcanvas, Spinner } from "react-bootstrap";
import Markdown from "react-markdown";
import { Link, useLocation } from "react-router";
import { HGContext } from "../../helpers/globals";
import { buildPageContext } from "./pageContext";
import { buildFullTranscript, buildSupportMailto, MailLabels, TranscriptTurn } from "./supportMailto";
import { ChatMessage, setAssistantOpen, takePendingQuestion, useAssistant, useAssistantOpen, usePendingQuestion } from "./useAssistant";
import { t } from "i18next";
import { ClipboardCheck } from "react-bootstrap-icons";

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

function CitationChip({ title, link, n }: { title: string; link: string; n: number }) {
  const label = `[${n}] ${title}`;
  const onClick = () => {
    // on small screens the panel covers the page, so get out of the way
    if (window.innerWidth < 768) setAssistantOpen(false);
  };
  if (/^https?:\/\//.test(link)) {
    return (
      <a className="assistant-chip badge rounded-pill text-bg-light" href={link} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }
  return (
    <Link className="assistant-chip badge rounded-pill text-bg-light" to={link} onClick={onClick}>
      {label}
    </Link>
  );
}

export function AssistantPanel() {
  const ctx = useContext(HGContext);
  const location = useLocation();
  const show = useAssistantOpen();
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);
  const bodyEnd = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const getContext = useCallback(() => buildPageContext(pathRef.current), []);
  const { messages, streaming, rateLimited, send, stop, reset } = useAssistant(getContext, ctx.globals.authUser?.uuid);

  // `?` opens the panel unless the user is typing somewhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?" || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      e.preventDefault();
      setAssistantOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // a question asked from outside the panel (the docs page) is sent once the panel is open and idle
  const pending = usePendingQuestion();
  useEffect(() => {
    if (!pending || !show || streaming) return;
    const q = takePendingQuestion();
    if (q) void send(q);
  }, [pending, show, streaming, send]);

  // when the panel is empty and waiting for a question, put the cursor in the input
  useEffect(() => {
    if (show && messages.length === 0) inputRef.current?.focus();
  }, [show, messages.length]);

  useEffect(() => {
    bodyEnd.current?.scrollIntoView?.({ block: "end" });
  }, [messages]);

  const pageCtx = buildPageContext(location.pathname);
  const labels: MailLabels = {
    question: "Q",
    answer: "A",
    page: t("general.pages"),
    language: t("congprofile.language"),
    congregation: t("list.congregation.title"),
    omitted: "omitted",
    subjectPrefix: t("general.subject"),
  };
  const turns: TranscriptTurn[] = messages
    .filter((m) => m.content)
    .map((m) => ({ role: m.role, content: m.content, citations: m.citations }));
  const congName = ctx.globals.cong?.name ?? "";

  const submit = () => {
    if (!input.trim() || streaming || rateLimited) return;
    const text = input;
    setInput("");
    void send(text);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildFullTranscript(turns, pageCtx, congName, labels));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // clipboard unavailable; the mailto link is still there
    }
  };

  const errorText = (m: ChatMessage) => {
    switch (m.error) {
      case "rate_limited":
        return t("assistant.rate-limited");
      case "disabled":
        return t("general.disabled");
      default:
        return t("form.error.try-later");
    }
  };

  const renderMessage = (m: ChatMessage, idx: number) => {
    if (m.role === "user") {
      return (
        <div key={m.id} className="assistant-msg assistant-msg-user">
          {m.content}
        </div>
      );
    }
    const upTo = turns.length ? messages.slice(0, idx + 1).filter((x) => x.content).length : 0;
    const mailto = buildSupportMailto(turns.slice(0, upTo), pageCtx, congName, labels);
    return (
      <div key={m.id} className="assistant-msg assistant-msg-ai">
        {!m.content && !m.done && !m.error && (
          <Spinner animation="border" size="sm" role="status" aria-label={t("general.processing")} />
        )}
        {m.content && <Markdown>{m.content}</Markdown>}
        {m.error && (
          <Alert variant="warning" className="mb-2 py-1">
            {errorText(m)}
          </Alert>
        )}
        {!!m.citations?.length && (
          <div className="assistant-citations">
            {m.citations.map((c) => (
              <CitationChip key={c.n} {...c} />
            ))}
          </div>
        )}
        {m.done && (
          <div className="assistant-actions">
            <a href={mailto}>{t("assistant.email-support")}</a>
            <Button variant="link" size="sm" className="p-0 ms-2" onClick={copy}>
              {copied && <ClipboardCheck className="me-1" />}
              {t("general.copy")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Offcanvas
      show={show}
      onHide={() => setAssistantOpen(false)}
      placement="end"
      // Non-modal: the rest of the app (e.g. the nav search) stays fully usable while the panel is open.
      backdrop={false}
      scroll
      enforceFocus={false}
      onEntered={() => {
        if (messages.length === 0) inputRef.current?.focus();
      }}
      className="assistant-panel"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{t("assistant.title")}</Offcanvas.Title>
        {messages.length > 0 && (
          <Button variant="outline-secondary" size="sm" className="assistant-new ms-auto me-2" onClick={reset}>
            {t("assistant.new-conversation")}
          </Button>
        )}
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column">
        <div className="assistant-messages grow">
          {/*{messages.length === 0 && (*/}
          {/*  <p className="text-muted">{"empty prompt" /*t("assistant.empty-prompt")*!/</p>*/}
          {/*)}*/}
          {messages.map(renderMessage)}
          <div ref={bodyEnd} />
        </div>
        <small className="text-muted my-2">{t("assistant.disclaimer")}</small>
        <Form
          className="d-flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Form.Control
            ref={inputRef}
            value={input}
            disabled={rateLimited}
            onChange={(e) => setInput(e.target.value)}
            maxLength={1000}
            placeholder={t("assistant.placeholder")}
            aria-label={t("assistant.placeholder")}
          />
          {streaming ? (
            <Button variant="secondary" onClick={stop}>
              {t("general.cancel")}
            </Button>
          ) : (
            <Button type="submit" disabled={!input.trim() || rateLimited}>
              {t("assistant.send")}
            </Button>
          )}
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
